import React, { Component } from 'react';
import { Plus, Edit2, Trash2, Move } from 'lucide-react';

const SAMPLE_VIDEOS = [
  "https://www.w3schools.com/html/mov_bbb.mp4",
  "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4"
];

class InteractiveVideoEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fragments: [],
      selectedFragment: null,
      selectedInteraction: null,
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      currentTime: 0,
      editMode: true
    };
    this.videoRef = React.createRef();
    this.containerRef = React.createRef();
  }

  addFragment = () => {
    const newFragment = {
      id: Date.now(),
      url: SAMPLE_VIDEOS[Math.floor(Math.random() * SAMPLE_VIDEOS.length)],
      interactions: [],
      title: `Fragmento ${this.state.fragments.length + 1}`
    };
    this.setState(prev => ({
      fragments: [...prev.fragments, newFragment],
      selectedFragment: newFragment
    }));
  };

  startDragging = (e, interaction) => {
    e.preventDefault();
    const videoRect = this.videoRef.current.getBoundingClientRect();
    const offsetX = e.clientX - videoRect.left - interaction.x;
    const offsetY = e.clientY - videoRect.top - interaction.y;
    
    this.setState({
      selectedInteraction: interaction,
      isDragging: true,
      dragOffset: { x: offsetX, y: offsetY }
    });

    document.addEventListener('mousemove', this.handleDrag);
    document.addEventListener('mouseup', this.stopDragging);
  };

  handleDrag = (e) => {
    if (!this.state.isDragging || !this.state.selectedInteraction) return;

    const videoRect = this.videoRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(
      videoRect.width - this.state.selectedInteraction.width,
      e.clientX - videoRect.left - this.state.dragOffset.x
    ));
    const y = Math.max(0, Math.min(
      videoRect.height - this.state.selectedInteraction.height,
      e.clientY - videoRect.top - this.state.dragOffset.y
    ));

    this.setState(prev => ({
      fragments: prev.fragments.map(f =>
        f.id === prev.selectedFragment.id
          ? {
              ...f,
              interactions: f.interactions.map(i =>
                i.id === prev.selectedInteraction.id
                  ? { ...i, x, y }
                  : i
              )
            }
          : f
      )
    }));
  };

  stopDragging = () => {
    this.setState({ isDragging: false });
    document.removeEventListener('mousemove', this.handleDrag);
    document.removeEventListener('mouseup', this.stopDragging);
  };

  addInteraction = () => {
    if (!this.state.selectedFragment) return;
    const videoRect = this.videoRef.current.getBoundingClientRect();
    
    const newInteraction = {
      id: Date.now(),
      startTime: Math.floor(this.state.currentTime),
      x: videoRect.width / 2 - 75,
      y: videoRect.height / 2 - 40,
      width: 150,
      height: 80,
      targetFragmentId: null,
      text: "Nova interação"
    };

    this.setState(prev => ({
      fragments: prev.fragments.map(f => 
        f.id === prev.selectedFragment.id 
          ? { ...f, interactions: [...f.interactions, newInteraction] }
          : f
      ),
      selectedInteraction: newInteraction
    }));
  };

  handleTimeUpdate = () => {
    this.setState({ currentTime: this.videoRef.current.currentTime });
  };

  seekToTime = (e) => {
    const video = this.videoRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const seekTime = (x / rect.width) * video.duration;
    video.currentTime = seekTime;
    this.setState({ currentTime: seekTime });
  };

  setInteractionTarget = (interaction, targetId) => {
    this.setState(prev => ({
      fragments: prev.fragments.map(f =>
        f.id === prev.selectedFragment.id
          ? {
              ...f,
              interactions: f.interactions.map(i =>
                i.id === interaction.id
                  ? { ...i, targetFragmentId: targetId }
                  : i
              )
            }
          : f
      )
    }));
  };

  deleteInteraction = (interaction) => {
    this.setState(prev => ({
      fragments: prev.fragments.map(f =>
        f.id === prev.selectedFragment.id
          ? {
              ...f,
              interactions: f.interactions.filter(i => i.id !== interaction.id)
            }
          : f
      ),
      selectedInteraction: null
    }));
  };

  render() {
    const { fragments, selectedFragment, selectedInteraction, currentTime } = this.state;

    return (
      <div className="w-full max-w-6xl mx-auto p-4" ref={this.containerRef}>
        <div className="flex gap-4">
          <div className="w-64 bg-gray-100 p-4 rounded">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold">Fragmentos</h3>
              <button
                onClick={this.addFragment}
                className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                <Plus size={16} />
              </button>
            </div>
            <div className="space-y-2">
              {fragments.map(fragment => (
                <div
                  key={fragment.id}
                  className={`p-2 rounded cursor-pointer ${
                    selectedFragment.id === fragment.id ? 'bg-blue-100' : 'bg-white'
                  }`}
                  onClick={() => this.setState({ selectedFragment: fragment })}
                >
                  {fragment.title}
                </div>
              ))}
            </div>
          </div>

          <div className="flex-1">
            {selectedFragment ? (
              <div className="space-y-4">
                <div className="relative">
                  <video
                    ref={this.videoRef}
                    src={selectedFragment.url}
                    className="w-full"
                    controls
                    onTimeUpdate={this.handleTimeUpdate}
                  />
                  <div className="absolute top-0 left-0 w-full h-full">
                    {selectedFragment.interactions.map(interaction => (
                      <div
                        key={interaction.id}
                        className={`absolute ${
                          selectedInteraction.id === interaction.id
                            ? 'ring-2 ring-blue-500'
                            : ''
                        }`}
                        style={{
                          left: `${interaction.x}px`,
                          top: `${interaction.y}px`,
                          width: `${interaction.width}px`,
                          height: `${interaction.height}px`,
                          cursor: 'move'
                        }}
                        onMouseDown={(e) => this.startDragging(e, interaction)}
                      >
                        <div className="bg-blue-500 bg-opacity-50 text-white p-2 rounded w-full h-full flex items-center justify-center">
                          {interaction.text}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative h-8 bg-gray-200 rounded cursor-pointer" onClick={this.seekToTime}>
                  <div
                    className="absolute h-full bg-blue-500"
                    style={{
                      width: `${(currentTime / (this.videoRef.current && this.videoRef.current.duration || 1)) * 100}%`
                    }}
                  />
                </div>

                <div className="flex justify-between items-center">
                  <button
                    onClick={this.addInteraction}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Adicionar Interação em {Math.floor(currentTime)}s
                  </button>
                </div>

                {selectedInteraction && (
                  <div className="bg-gray-100 p-4 rounded">
                    <h4 className="font-bold mb-4">Editar Interação</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block mb-2">Texto</label>
                        <input
                          type="text"
                          value={selectedInteraction.text}
                          onChange={(e) => {
                            const newText = e.target.value;
                            this.setState(prev => ({
                              fragments: prev.fragments.map(f =>
                                f.id === selectedFragment.id
                                  ? {
                                      ...f,
                                      interactions: f.interactions.map(i =>
                                        i.id === selectedInteraction.id
                                          ? { ...i, text: newText }
                                          : i
                                      )
                                    }
                                  : f
                              )
                            }));
                          }}
                          className="w-full p-2 border rounded"
                        />
                      </div>
                      <div>
                        <label className="block mb-2">Link para Fragmento</label>
                        <select
                          value={selectedInteraction.targetFragmentId || ''}
                          onChange={(e) => this.setInteractionTarget(selectedInteraction, e.target.value)}
                          className="w-full p-2 border rounded"
                        >
                          <option value="">Selecione um fragmento</option>
                          {fragments.filter(f => f.id !== selectedFragment.id).map(f => (
                            <option key={f.id} value={f.id}>{f.title}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={() => this.deleteInteraction(selectedInteraction)}
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Excluir Interação
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-gray-100 rounded">
                <p className="text-gray-500">Selecione ou adicione um fragmento</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default InteractiveVideoEditor;